import { useEffect, useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiAlertCircleOutline } from '@mdi/js'
import { PageHeader } from './shared/PageHeader'
import { Button } from './ui/button'
import { useOptionalFeedbackContext } from './feedback/FeedbackContext'
import { canAutoReload, isStaleChunkError, reloadForStaleChunk } from '../lib/staleChunk'

// PageCrashed is shown in place of a page whose render threw: the router's
// defaultErrorComponent (per route) and PageErrorBoundary (root outlet). The
// shell around it keeps working. Every string has an English default so the
// component still reads correctly if i18n itself is what crashed.
export function PageCrashed({ error }: { error?: unknown }) {
  const { t } = useTranslation()
  const { org } = useParams({ strict: false }) as { org?: string }
  const feedback = useOptionalFeedbackContext()
  const message = error instanceof Error ? error.message : error ? String(error) : ''
  // A chunk of the previous deploy failed to load (#1719): reload once on
  // the same URL instead of showing the crash page. Decided once per mount,
  // so the reload's own timestamp doesn't flip it back.
  const [reloading] = useState(() => isStaleChunkError(error) && canAutoReload())
  useEffect(() => {
    if (reloading) reloadForStaleChunk()
  }, [reloading])

  if (reloading) return null

  return (
    <div
      role="alert"
      data-testid="page-crashed"
      className="mx-auto flex max-w-xl flex-col items-center gap-4 py-16 text-center"
    >
      <PageHeader
        icon={mdiAlertCircleOutline}
        title={t('page_crashed.title', { defaultValue: 'This page crashed' })}
        description={t('page_crashed.description', {
          defaultValue: 'Something went wrong while displaying this page. The rest of the application still works.',
        })}
        data-testid="page-crashed-title"
      />
      {import.meta.env.DEV && message && (
        <pre
          data-testid="page-crashed-error"
          className="max-w-full overflow-auto rounded-md bg-muted p-3 text-left text-xs"
        >
          {message}
        </pre>
      )}
      <div className="flex flex-wrap justify-center gap-2">
        <Button data-testid="page-crashed-reload" onClick={() => window.location.reload()}>
          {t('page_crashed.reload', { defaultValue: 'Reload page' })}
        </Button>
        {org && (
          <Button asChild variant="outline">
            <a href={`/${org}`} data-testid="page-crashed-home">
              {t('page_crashed.home', { defaultValue: 'Back to home' })}
            </a>
          </Button>
        )}
        {feedback && (
          <Button variant="outline" data-testid="page-crashed-report" onClick={() => feedback.open()}>
            {t('page_crashed.report', { defaultValue: 'Report this problem' })}
          </Button>
        )}
      </div>
    </div>
  )
}
